import { useEffect, useRef, useState } from "react"
import type { MouseEvent } from "react"
import { createPortal } from "react-dom"
import classnames from "classnames"
import parse from "html-react-parser"

import ServiceList from "./service_list"

import arrowIcon from "images/icons/dropdown_arrow.svg?raw"
import copyIcon from "images/icons/copy.svg?raw"

import styles from "./ingredient_listbox.module.scss"

interface Props {
  apiList: ApiList
  name: string
  serviceList?: Service[]
}

interface ApiList {
  options: Array<string> | Record<string, Array<string>>
  placeholder: string
  helpText?: {
    [key: string]: string
  }
}

interface Service {
  image: string
  module_name: string
  name: string
  normalized_module_name: string
}

const COPIED_TOAST_MS = 1500

const IngredientListbox = (props: Props) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(copiedTimerRef.current), [])

  if (!props.apiList?.options) return null

  // Selecting the label is the visible trace of the copy, as the frame draws it.
  const copyContent = (e: MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget

    const label = button.querySelector("span")
    const selection = window.getSelection()
    if (label && selection) {
      const range = document.createRange()
      range.selectNodeContents(label)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    navigator.clipboard
      .writeText(button.title)
      .then(() => {
        setCopied(true)
        clearTimeout(copiedTimerRef.current)
        copiedTimerRef.current = setTimeout(() => setCopied(false), COPIED_TOAST_MS)
      })
      .catch(() => {})
  }

  // As on iOS: the full name every time, the path on one line with its trailing
  // dot and the member (ingredient or call) bold on its own.
  const ingredient = (name: string) => {
    const memberAt = name.lastIndexOf(".") + 1

    return (
      <li key={name}>
        <button type="button" className={styles.ingredient} title={name} onClick={copyContent}>
          {parse(copyIcon)}
          <span>
            {memberAt > 0 && <span className={styles.path}>{name.slice(0, memberAt)}</span>}
            {name.slice(memberAt)}
          </span>
        </button>
      </li>
    )
  }

  const names = (options: Array<string> | Record<string, Array<string>>) =>
    Array.isArray(options)
      ? options
      : Object.entries(options).flatMap(([serviceName, members]) => members.map(member => `${serviceName}.${member}`))

  const renderLimits = () => {
    if (!props.apiList.helpText) return null

    return (
      <ul className={styles["help-text"]}>
        {Object.values(props.apiList.helpText).map(value => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    )
  }

  return (
    <section className={styles.menu}>
      <header className={styles.title}>
        <h3 className={classnames("text-h4 phone-text-h5", styles.name)}>{props.name}</h3>
        {props.serviceList?.length ? (
          <span className={styles.services}>
            <ServiceList context={props.name} services={props.serviceList} />
          </span>
        ) : null}
      </header>

      <div className={styles.box}>
        <button type="button" className={styles.toggle} aria-expanded={open} onClick={() => setOpen(!open)}>
          <span>{props.apiList.placeholder}</span>
          <span className={styles.arrow}>{parse(arrowIcon)}</span>
        </button>
        {open && (
          <>
            <ul className={styles.ingredients}>{names(props.apiList.options).map(ingredient)}</ul>
            {renderLimits()}
          </>
        )}
      </div>

      {/* The live region stays mounted: text dropped into a fresh one is not announced. */}
      {createPortal(
        <div role="status" className={copied ? styles.toast : styles["toast-idle"]}>
          {copied && "Copied to clipboard"}
        </div>,
        document.body,
      )}
    </section>
  )
}

export default IngredientListbox
